import {
  FieldMappings,
  NestedPropertyMapping,
  PropertyMapping,
} from '../types/DataSyncItem';
import { FieldMappingEntry } from '../types/DataSyncViewModel';
import {
  FIELD_PATH_SEPARATOR,
  FieldDefinition,
} from '../types/FieldDefinition';
import { findFieldByPath } from '../lib/dataSync';

interface TreeNode {
  children: Map<string, TreeNode>;
  sourcePaths: string[][];
  destPath?: string[]; // Destination path from root to this node
}

function createNode(): TreeNode {
  return { children: new Map(), sourcePaths: [] };
}

function splitPath(path: string): string[] {
  return path.split(FIELD_PATH_SEPARATOR).filter(Boolean);
}

function jsonPath(segments: string[]): string {
  return '$' + segments.map((s) => `['${s}']`).join('');
}

function commonPrefix(paths: string[][]): string[] {
  if (paths.length === 0) return [];
  let prefix = paths[0];
  for (const p of paths.slice(1)) {
    let i = 0;
    while (i < prefix.length && i < p.length && prefix[i] === p[i]) i++;
    prefix = prefix.slice(0, i);
    if (prefix.length === 0) break;
  }
  return prefix;
}

/**
 * Recursively build property structure.
 * @param node The current tree node
 * @param parentSourcePrefix The prefix path of parent's root_object_field
 * @param destinationFields Optional destination field definitions for array detection
 * @param sourceFields Optional source field definitions for array detection
 */
function buildProperties(
  node: TreeNode,
  parentSourcePrefix: string[],
  destinationFields?: FieldDefinition[],
  sourceFields?: FieldDefinition[]
): Record<string, PropertyMapping> {
  const result: Record<string, PropertyMapping> = {};

  for (const [key, child] of node.children.entries()) {
    result[key] = buildPropertyMapping(
      child,
      parentSourcePrefix,
      destinationFields,
      sourceFields
    );
  }

  return result;
}

function buildPropertyMapping(
  node: TreeNode,
  parentSourcePrefix: string[],
  destinationFields?: FieldDefinition[],
  sourceFields?: FieldDefinition[]
): PropertyMapping {
  const hasChildren = node.children.size > 0;
  const leafSourcePaths = node.sourcePaths;

  // Gather all descendant source paths (for prefix/root computation)
  const allDescendantPaths = gatherAllSourcePaths(node);

  // --- Case 1: Pure leaf ---
  if (!hasChildren && leafSourcePaths.length > 0) {
    const [sourcePath] = leafSourcePaths;
    const relative = relativePath(sourcePath, parentSourcePrefix);
    return { source_field: jsonPath(relative) };
  }

  // --- Case 2: Nested structure ---
  const nestedProps = buildProperties(node, parentSourcePrefix, destinationFields, sourceFields);
  const childCount = Object.keys(nestedProps).length;

  // Check if this destination field is an array
  const isArray =
    node.destPath && destinationFields
      ? isDestinationArray(node.destPath, destinationFields)
      : false;

  // Determine root_object_field from descendant source paths
  let rootPrefix = commonPrefix(allDescendantPaths);

  // Ensure the prefix doesn't consume the entire path for any leaf child.
  // Each leaf child needs at least one segment remaining for its source_field.
  // If the prefix equals the full path of any descendant, trim it by one segment.
  if (rootPrefix.length > 0) {
    const prefixConsumesFullPath = allDescendantPaths.some(
      (path) =>
        path.length === rootPrefix.length &&
        path.every((segment, i) => segment === rootPrefix[i])
    );
    if (prefixConsumesFullPath && rootPrefix.length > 1) {
      rootPrefix = rootPrefix.slice(0, -1);
    }
  }

  // For array fields, find the position of the corresponding array in source paths
  if (isArray && node.destPath && rootPrefix.length > 0 && sourceFields && destinationFields) {
    // Count how many arrays exist in the destination path up to this node
    const arrayCountInDest = countArraysInDestPath(node.destPath, destinationFields);

    // Find the position of the Nth array in the source paths
    // We'll use the first source path as a representative
    const [firstSourcePath] = allDescendantPaths;
    const nthArrayPosition = findNthArrayInSourcePath(
      firstSourcePath,
      arrayCountInDest,
      sourceFields
    );

    // If we found the array position, trim to include up to and including that position
    if (nthArrayPosition >= 0) {
      rootPrefix = rootPrefix.slice(0, nthArrayPosition + 1);
    }
  }

  // Should we include root_object_field?
  const includeRoot =
    isArray || // Force root_object_field for array fields
    childCount > 1 ||
    node.children.size > 1 ||
    (node.sourcePaths.length > 0 && hasChildren);

  const nested: NestedPropertyMapping = { properties: nestedProps };

  if (includeRoot && rootPrefix.length > 0) {
    const rootField = jsonPath(rootPrefix);
    nested.root_object_field = rootField;

    // Now compute children again, relative to this new root
    nested.properties = buildProperties(node, rootPrefix, destinationFields, sourceFields);
  }

  return { nested };
}

function gatherAllSourcePaths(node: TreeNode): string[][] {
  let paths = [...node.sourcePaths];
  for (const child of node.children.values()) {
    paths = paths.concat(gatherAllSourcePaths(child));
  }
  return paths;
}

/** Compute path relative to prefix */
function relativePath(path: string[], prefix: string[]): string[] {
  let i = 0;
  while (i < prefix.length && i < path.length && path[i] === prefix[i]) {
    i++;
  }
  return path.slice(i);
}

/**
 * Check if a destination path represents an array field
 */
function isDestinationArray(
  destPath: string[],
  destinationFields?: FieldDefinition[]
): boolean {
  if (!destinationFields || destPath.length === 0) {
    return false;
  }

  const pathString = destPath.join(FIELD_PATH_SEPARATOR);
  const field = findFieldByPath(destinationFields, pathString);

  if (!field) {
    return false;
  }

  return field.array !== undefined;
}

/**
 * Count how many arrays exist in a destination path
 */
function countArraysInDestPath(
  destPath: string[],
  destinationFields?: FieldDefinition[]
): number {
  if (!destinationFields) {
    return 0;
  }

  let count = 0;
  for (let i = 0; i < destPath.length; i++) {
    const partialPath = destPath.slice(0, i + 1);
    if (isDestinationArray(partialPath, destinationFields)) {
      count++;
    }
  }
  return count;
}

/**
 * Find the position of the Nth array in a source path
 * Returns the index of the Nth array field, or -1 if not found
 */
function findNthArrayInSourcePath(
  sourcePath: string[],
  n: number,
  sourceFields?: FieldDefinition[]
): number {
  if (!sourceFields || n < 1) {
    return -1;
  }

  let arrayCount = 0;
  for (let i = 0; i < sourcePath.length; i++) {
    const partialPath = sourcePath.slice(0, i + 1);
    const pathString = partialPath.join(FIELD_PATH_SEPARATOR);
    const field = findFieldByPath(sourceFields, pathString);

    if (field && field.array !== undefined) {
      arrayCount++;
      if (arrayCount === n) {
        return i;
      }
    }
  }

  return -1;
}

function normalizeRootObjectFields(
  fieldMappings: FieldMappings
): FieldMappings {
  if (!fieldMappings.nested_mappings) {
    return fieldMappings;
  }

  const traverse = (nested: NestedPropertyMapping, parentRoot?: string) => {
    if (nested.root_object_field) {
      if (parentRoot && nested.root_object_field.includes(parentRoot)) {
        if (nested.root_object_field === parentRoot) {
          delete nested.root_object_field;
        } else {
          const newParentRoot = nested.root_object_field;
          nested.root_object_field = nested.root_object_field.replace(
            parentRoot,
            '$'
          );
          parentRoot = newParentRoot;
        }
      } else {
        parentRoot = nested.root_object_field;
      }
    }

    Object.values(nested.properties).forEach((prop) => {
      if (prop.nested) {
        traverse(prop.nested, parentRoot);
      }
    });
  };

  Object.values(fieldMappings.nested_mappings.properties).forEach((prop) => {
    if (prop.nested) {
      traverse(prop.nested);
    }
  });
  return fieldMappings;
}

export class FieldMappingConverter {
  /**
   * Converts legacy flat mappings to proto-compliant nested mapping format
   * @param fieldMappings The FieldMappings object containing legacy mappings
   * @returns FieldMappings with nested_mappings populated and mappings preserved for backward compatibility
   */
  public static convertToNestedMappings(
    fieldMappings: FieldMappings
  ): FieldMappings {
    if (!fieldMappings.mappings) {
      return {
        ...fieldMappings,
        nested_mappings: {
          properties: {},
        },
      };
    }

    const nestedProperties: { [key: string]: PropertyMapping } = {};

    Object.entries(fieldMappings.mappings).forEach(
      ([destinationField, mapping]) => {
        if (!mapping.disabled && mapping.source_field) {
          // Parse the destination field path to create proper nested structure
          this.setPropertyMapping(
            nestedProperties,
            destinationField,
            mapping.source_field
          );
        }
      }
    );

    return {
      ...fieldMappings,
      nested_mappings: {
        properties: nestedProperties,
      },
    };
  }

  /**
   * Convert array of UI mappings into FieldMappings according to rules described.
   * @param uiMappings Array of field mapping entries from UI
   * @param sourceFields Optional source field definitions for array detection
   * @param destinationFields Optional destination field definitions for array detection
   */
  public static convertFromUIFormat(
    uiMappings: FieldMappingEntry[],
    sourceFields?: FieldDefinition[],
    destinationFields?: FieldDefinition[]
  ): FieldMappings {
    const root: TreeNode = createNode();

    for (const { destinationField, sourceField } of uiMappings) {
      const destParts = splitPath(destinationField);
      const currentPath: string[] = [];
      let node = root;

      for (const part of destParts) {
        currentPath.push(part);
        if (!node.children.has(part)) {
          const newNode = createNode();
          newNode.destPath = [...currentPath];
          node.children.set(part, newNode);
        }
        node = node.children.get(part)!;
      }
      node.sourcePaths.push(splitPath(sourceField));
    }

    return normalizeRootObjectFields({
      nested_mappings: {
        properties: buildProperties(root, [], destinationFields, sourceFields),
      },
    });
  }

  /**
   * Gets all field mappings as a flat array for UI components
   * @param fieldMappings The FieldMappings object
   * @returns Array of FieldMappingEntry objects with / delimiter for nested fields
   */
  public static getFieldMappingsForUI(
    fieldMappings: FieldMappings
  ): FieldMappingEntry[] {
    const result: FieldMappingEntry[] = [];

    if (fieldMappings.nested_mappings) {
      this.extractMappingsForUI(
        fieldMappings.nested_mappings.properties,
        result
      );
    }

    return result;
  }

  /**
   * Sets a property mapping in the nested structure, creating intermediate objects as needed
   * @param properties The properties object to update
   * @param destinationPath The destination field path (e.g., "product.variants.sku")
   * @param sourcePath The source field path (e.g., "category.name")
   */
  private static setPropertyMapping(
    properties: { [key: string]: PropertyMapping },
    destinationPath: string,
    sourcePath: string,
    disabled?: boolean
  ): void {
    const pathParts = destinationPath.split(FIELD_PATH_SEPARATOR);

    if (pathParts.length === 1) {
      // Simple root-level mapping
      properties[pathParts[0]] = {
        source_field: this.convertToJsonPath(sourcePath),
      };
    } else {
      // For now, just store as a flat mapping with the full JSONPath
      // We'll implement proper nested structure serialization at save time
      properties[destinationPath] = {
        source_field: this.convertToJsonPath(sourcePath),
      };
    }
  }

  /**
   * Recursively extracts mappings for UI from nested mapping properties
   * @param properties The nested mapping properties
   * @param result The result array to populate with mappings
   * @param parentDestinationPath The parent path for nested fields
   */
  private static extractMappingsForUI(
    properties: { [key: string]: PropertyMapping },
    result: Array<{
      destinationField: string;
      sourceField: string;
      disabled: boolean;
    }>,
    parentDestinationPath: string = '',
    parentSourcePath: string = ''
  ): void {
    Object.entries(properties).forEach(([fieldName, mapping]) => {
      const fullDestinationFieldPath = parentDestinationPath
        ? `${parentDestinationPath}${FIELD_PATH_SEPARATOR}${fieldName}`
        : fieldName;

      if (mapping.source_field) {
        const sourceFieldPath = this.convertFromJsonPath(mapping.source_field);
        const fullSourcePath = parentSourcePath
          ? `${parentSourcePath}${FIELD_PATH_SEPARATOR}${sourceFieldPath}`
          : sourceFieldPath;

        result.push({
          destinationField: fullDestinationFieldPath,
          sourceField: fullSourcePath,
          disabled: false,
        });
      }

      if (mapping.nested?.properties) {
        let newParentSourcePath = parentSourcePath;

        if (mapping.nested.root_object_field) {
          const rootPath = this.convertFromJsonPath(
            mapping.nested.root_object_field
          );

          // Check if root_object_field contains multiple segments (absolute path)
          // or just one segment (relative path)
          const rootPathParts = rootPath
            .split(FIELD_PATH_SEPARATOR)
            .filter(Boolean);

          if (rootPathParts.length > 1 || !parentSourcePath) {
            // Absolute path - use it directly, don't combine with parent
            newParentSourcePath = rootPath;
          } else {
            // Relative path (single segment) - combine with parent
            newParentSourcePath = parentSourcePath
              ? `${parentSourcePath}${FIELD_PATH_SEPARATOR}${rootPath}`
              : rootPath;
          }
        }

        this.extractMappingsForUI(
          mapping.nested.properties,
          result,
          fullDestinationFieldPath,
          newParentSourcePath
        );
      }
    });
  }

  /**
   * Converts a simple field path to JSONPath bracket notation format
   * @param fieldPath Simple field path like "category.name" or bracket notation like "$['category']['name']"
   * @returns JSONPath bracket notation format like "$['category']['name']"
   */
  private static convertToJsonPath(fieldPath: string): string {
    if (fieldPath.startsWith('$[')) {
      return fieldPath;
    }
    const parts = fieldPath.split(FIELD_PATH_SEPARATOR);
    return '$' + parts.map((part) => `['${part}']`).join('');
  }

  /**
   * Converts JSONPath bracket notation back to simple field path
   * @param json_path JSONPath bracket notation like "$['category']['name']"
   * @returns Simple field path like "category/name"
   */
  private static convertFromJsonPath(json_path: string): string {
    if (json_path.startsWith('$[')) {
      // Convert bracket notation to simple field path
      const bracketMatches = json_path.match(/\['([^']+)'\]/g);
      if (bracketMatches) {
        const parts = bracketMatches.map((match) => match.slice(2, -2)); // Remove [' and ']
        return parts.join(FIELD_PATH_SEPARATOR);
      }
    }
    return json_path;
  }
}
